import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { useStreetViewPanoramaLocation } from '~/_shared/components/streetViewPanorama/useStreetViewPanoramaLocation';
import { useTranslation } from '~/_shared/utils/hooks';
import { useLocationImageGallery } from '~/customizeLocationPanel/useLocationImageGallery';
import { useModal } from '~/modal/useModal.hook';
import { activeMapElementsClearState } from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import { frontendStateStreetViewSet } from '~/store/frontendState/mapTools/streetView/streetView.actionCreators';
import { useActivePlaceSelector } from '~/store/frontendState/places/places.selectors';
import { mapSettingsPlacesRemovePlaceFromSearch } from '~/store/mapSettings/places/mapSettingsPlaces.actionCreators';
import { useMapSettingsSettingsUnitSystemSelector } from '~/store/mapSettings/settings/mapSettingsSettings.selectors';
import { useIsMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';
import { useGetPlaceFromActivePlace } from '../../mapTools/placesTool/hooks/useGetPlaceFromActivePlace';
import { usePlaceDetails } from '../../mapTools/placesTool/hooks/usePlaceDetails';
import { PlaceDetailsComponent } from './placeDetails.component';
import { getPlaceDetailRows } from './placeDetails.helpers';

export const PlaceDetailsContainer = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const activePlace = useActivePlaceSelector();
  const isPresentationalMap = useIsMapPresentationalSelector();
  const unitSystem = useMapSettingsSettingsUnitSystemSelector();
  const { openModal: openImageViewModal } = useModal('ImagesView');

  const {
    placeDataLoaded,
    place,
    placeIndex,
    placeSearch,
  } = useGetPlaceFromActivePlace(activePlace);

  const { placeDetail, isLoading: placeDetailLoading } = usePlaceDetails({
    placeId: place?.id,
    provider: placeSearch?.provider,
  });

  const {
    streetViewLocation,
    isLoading: streetViewLocationLoading,
  } = useStreetViewPanoramaLocation(placeDataLoaded && place ? place.location : null);

  const openStreetView = useCallback(() => {
    if (streetViewLocation) {
      dispatch(frontendStateStreetViewSet(streetViewLocation));
    }
  }, [dispatch, streetViewLocation]);

  const images = useMemo(() => (
    placeDetail?.photos?.map(photo => ({
      original: photo.url,
      thumbnail: photo.thumbnail,
    })) ?? []
  ), [placeDetail?.photos]);

  const openImagesView = useCallback((imageIndex: number) => {
    openImageViewModal({ startingImageIndex: imageIndex, images });
  }, [images, openImageViewModal]);

  const gallery = useLocationImageGallery({
    images,
    onImageClick: openImagesView,
    onStreetViewClick: openStreetView,
    streetViewLocation: !streetViewLocationLoading ? streetViewLocation : null,
  });

  const detailRows = useMemo(() => (
    placeDetail ? getPlaceDetailRows({ placeDetail, distanceUnit: unitSystem, t }) : []
  ), [placeDetail, t, unitSystem]);

  if (!placeDataLoaded || !placeSearch || !place) {
    return null;
  }

  const onRemovePlaceFromSearch = () => {
    dispatch(activeMapElementsClearState()); // close the right sidebar
    dispatch(mapSettingsPlacesRemovePlaceFromSearch({ searchId: placeSearch.id, placeId: place.id }));
  };

  return (
    <PlaceDetailsComponent
      placeSearch={placeSearch}
      placeIndex={placeIndex}
      place={place}
      detailRows={detailRows}
      areDetailsLoading={placeDetailLoading}
      onClose={onClose}
      onRemoveFromSearchResults={isPresentationalMap ? undefined : onRemovePlaceFromSearch}
      gallery={gallery.galleryItems.length > 0 ? gallery : undefined}
    />
  );
};
